import React, { HTMLAttributes, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  adyenGetPaymentMethods,
  getAvailablePaymentMethodType,
} from "../../../apis/subscription";
import type {
  IGetPaymentGatewayTypeResponse,
  IStoredPaymentMethod,
} from "../../../apis/subscription/type";
import { ReactComponent as AlipayhkIcon } from "../../../assets/icons/Alipayhk.svg";
import { ReactComponent as FPSIcon } from "../../../assets/icons/FPS.svg";
import { ReactComponent as PaymeIcon } from "../../../assets/icons/Payme.svg";
import { ReactComponent as CreditCardIcon } from "../../../assets/icons/creditCard.svg";

import { customShowToast } from "../../../helper/toast";
import useGeneralStore from "../../../store/general";
import AdyenAlipayHK from "../../atom/adyenAlipayHK";
import AdyenCreditCardElement, {
  type IAdyenCreditCardInfo,
} from "../../atom/adyenCreditCard";
import AdyenPayme from "../../atom/adyenPayme";
import AdyenStoredCard from "../../atom/adyenStoredCard";
import AirWallexFPS from "../../atom/airwallexFPS";
import BaseButton from "../../atom/baseButton";
import BaseLabel from "../../atom/baseLabel";

export interface IPaymentMethodsInfo {
  paymentMethodToken: string;
  adyenCreditCardInfo: IAdyenCreditCardInfo;
}

interface ISinglePaymentMethodSelectionBlock {
  adyenCheckout: (value?: IPaymentMethodsInfo) => void;
  buttonDisabled?: boolean;
  reInitPaymentMethod?: boolean;
  bindedCard?: {
    shopperReference: string;
    storedPaymentMethodId: string;
  };
  paymentId?: string;

  hideCreditCard?: boolean;
}

type TSinglePaymentMethodSelectionBlockProps =
  ISinglePaymentMethodSelectionBlock & HTMLAttributes<HTMLDivElement>;

const SinglePaymentMethodSelectionBlock: React.FC<
  TSinglePaymentMethodSelectionBlockProps
> = (props) => {
  const {
    adyenCheckout,
    buttonDisabled = false,
    reInitPaymentMethod,
    bindedCard,
    paymentId,

    hideCreditCard = false,
    ...otherProps
  } = props;

  // available payment methods
  const [availablePaymentMethod, setAvailablePaymentMethod] = useState<
    IGetPaymentGatewayTypeResponse[] | undefined | null
  >(undefined);

  // selected payment method
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    IGetPaymentGatewayTypeResponse | undefined
  >(undefined);

  // stored adyen credit card
  const [storedPaymentMethods, setStoredPaymentMethods] = useState<
    IStoredPaymentMethod | undefined | null
  >(undefined);

  const setGlobalLoadingState = useGeneralStore((state) => state.setIsLoading);

  const fetchAvailablePaymentMethods = async () => {
    try {
      const methodList = await getAvailablePaymentMethodType();
      setAvailablePaymentMethod(methodList);
    } catch (error) {}
  };

  const fetchStoredPaymentMethod = async (
    shopperRef: string,
    storedPaymentMethodId: string
  ) => {
    try {
      if (shopperRef) {
        setGlobalLoadingState(true);
        setStoredPaymentMethods(null);
        let paymentMethodObject = await adyenGetPaymentMethods(
          shopperRef,
          storedPaymentMethodId
        );

        if (paymentMethodObject) {
          setStoredPaymentMethods(paymentMethodObject);

          setGlobalLoadingState(false);
        }
      }
    } catch (error) {
      setStoredPaymentMethods(undefined);
      setGlobalLoadingState(false);
    }
  };

  useEffect(() => {
    fetchAvailablePaymentMethods();
  }, []);

  // fetch stored payment method if bindedCard is provided
  useEffect(() => {
    if (
      bindedCard &&
      bindedCard.shopperReference &&
      bindedCard.storedPaymentMethodId
    ) {
      fetchStoredPaymentMethod(
        bindedCard.shopperReference,
        bindedCard.storedPaymentMethodId
      );
    }
  }, [bindedCard?.shopperReference, bindedCard?.storedPaymentMethodId]);

  // reinitialize payment method
  useEffect(() => {
    if (reInitPaymentMethod) {
      console.log("reinit payment method");
      setSelectedPaymentMethod(undefined);
    }
  }, [reInitPaymentMethod]);

  return (
    <div className="flex flex-col w-full gap-4 text-black" {...otherProps}>
      <BaseLabel text={"請選擇付款方式"} />
      <div className="flex flex-row items-center gap-2">
        {availablePaymentMethod?.map((method) => {
          return (
            <div
              key={method.id}
              className={twMerge(
                "flex p-1 bg-white rounded cursor-pointer h-[36px] w-[56px]",
                selectedPaymentMethod?.id === method.id
                  ? "border-mainYellow border-[3px]"
                  : "border-white border-[3px]",
                method.id === 5 && hideCreditCard
                  ? "bg-mainDisable cursor-not-allowed"
                  : ""
              )}
              onClick={() => {
                if (method.id === 5 && hideCreditCard) {
                  customShowToast("請先前往綁定信用卡", "error");
                  return;
                }
                setSelectedPaymentMethod(method);
              }}
            >
              {method.id === 5 && <CreditCardIcon width={48} />}
              {method.id === 9 && <AlipayhkIcon width={48} />}
              {method.id === 10 && <PaymeIcon width={48} />}
              {method.id === 6 && <FPSIcon width={48} />}
            </div>
          );
        })}
      </div>

      {/* init credit card component */}
      {selectedPaymentMethod?.id === 5 && !bindedCard && !hideCreditCard && (
        <AdyenCreditCardElement
          onCardElementSubmit={(state) =>
            adyenCheckout({
              paymentMethodToken: selectedPaymentMethod.token,
              adyenCreditCardInfo: state,
            })
          }
          buttonDisabled={buttonDisabled}
        />
      )}

      {/* use existing card */}
      {selectedPaymentMethod?.id === 5 && bindedCard && (
        <div className="flex flex-col gap-4 text-white">
          <AdyenStoredCard storedPaymentMethods={storedPaymentMethods} />
          <BaseButton onClick={() => adyenCheckout()}>繳付帳單</BaseButton>
        </div>
      )}

      {/* init alipayhk component */}
      {selectedPaymentMethod?.id === 9 && (
        <AdyenAlipayHK paymentId={paymentId} />
      )}

      {/* init payme component */}
      {selectedPaymentMethod?.id === 10 && <AdyenPayme paymentId={paymentId} />}

      {/* init FPS component */}
      {selectedPaymentMethod?.id === 6 && (
        <AirWallexFPS paymentId={paymentId} />
      )}
    </div>
  );
};

export default SinglePaymentMethodSelectionBlock;
